import { Box } from '@material-ui/core';
import React from 'react';

import { ConditionalWrapper } from '@/components/shared/components/ConditionalWrapper';

import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { CopyToClipboardButton } from './CopyToClipboardButton';
import { CustomDivider } from './CustomDivider';
import { Font } from './Font';
import { StyledTooltip } from './StyledTooltip';
import { Text } from './Text';

interface ReadOnlyDataRowProps {
  labelTextId: string;
  value: string | number | JSX.Element | null | undefined;
  labelDynamicDataTexts?: string[];
  strongTitle?: boolean;
  valueUnit?: React.ReactNode | string;
  labelIcon?: string;
  labelIconPosition?: 'start' | 'end';
  valueStartIcon?: JSX.Element;
  tooltipTitle?: string | number | JSX.Element;
  underlined?: boolean;
  forceSingleLine?: boolean;
  wrap?: boolean;
  copyButton?: boolean;
  boxProps?: React.ComponentProps<typeof Box>;
}

export function ReadOnlyDataRow({
  labelTextId,
  value,
  labelDynamicDataTexts,
  strongTitle,
  valueUnit,
  labelIcon,
  labelIconPosition = 'end',
  valueStartIcon,
  tooltipTitle,
  underlined = false,
  forceSingleLine = false,
  wrap = false,
  copyButton = false,
  boxProps,
}: ReadOnlyDataRowProps) {
  const { palette } = useCustomTheme();

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} {...boxProps}>
        <Box
          py={underlined ? 1 : '2px'}
          flexGrow={1}
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={forceSingleLine ? 'end' : 'baseline'}
        >
          <ConditionalWrapper
            condition={!!tooltipTitle}
            wrapper={(children) => (
              <StyledTooltip title={tooltipTitle as string} tooltipProps={{ placement: 'right' }}>
                {children}
              </StyledTooltip>
            )}
          >
            <Box display={'inline-flex'} alignItems={'center'} pr={1}>
              {labelIcon && labelIconPosition === 'start' && (
                <i
                  className={'material-icons-outlined'}
                  style={{ fontSize: 16, color: palette.grey[400], lineHeight: 1, paddingRight: 6 }}
                >
                  {labelIcon}
                </i>
              )}
              <Font
                className={forceSingleLine ? 'overflow-text-ellipsis' : ''}
                variant="subtitle2"
                color="textSecondary"
              >
                <Text strong={strongTitle} tId={labelTextId} dynamicDataTexts={labelDynamicDataTexts} />
              </Font>
              {labelIcon && labelIconPosition === 'end' && (
                <i
                  className={'material-icons-outlined'}
                  style={{ fontSize: 16, color: palette.grey[400], lineHeight: 1, paddingLeft: 6 }}
                >
                  {labelIcon}
                </i>
              )}
              {copyButton && value && <CopyToClipboardButton value={value.toString()} />}
            </Box>
          </ConditionalWrapper>
          <Font
            className={`${forceSingleLine ? 'overflow-text-ellipsis' : ''} ${wrap ? 'wrap-text' : ''}`}
            variant="h5"
            color="textPrimary"
            style={{ textAlign: 'right', paddingLeft: '8px' }}
          >
            {valueStartIcon} {value || value === 0 ? value : '-'} {valueUnit}
          </Font>
        </Box>
        {underlined && <CustomDivider orientation={'horizontal'} margin={0} />}
      </Box>
    </>
  );
}
